import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CASE_STUDIES_LIST } from '../mock/caseStudiesData';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { BrandsTicker } from '../components/Marquees';
import {
  ChevronRight,
  ArrowRight,
  Sparkles,
  Award,
  Building2,
  MapPin,
  Clock,
  Package,
  ShieldCheck,
  Truck,
  HeartHandshake,
} from 'lucide-react';

const CaseStudiesIndex = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Case Studies' },
    { id: 'Diwali & Festive Gifting', label: 'Diwali & Festive Rush' },
    { id: 'Employee Experience & Retention', label: 'Remote Teams & GaaS' },
    { id: 'Client Gifting & Relationship Management', label: 'Client Retention' },
  ];

  const filteredStudies = selectedCategory === 'all'
    ? CASE_STUDIES_LIST
    : CASE_STUDIES_LIST.filter((s) => s.category === selectedCategory);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://wrapyourwish.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Case Studies',
        item: 'https://wrapyourwish.com/case-studies',
      },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Corporate Gifting Case Studies & Client Stories',
    description: 'Explore verified client case studies highlighting how Wrap Your Wish solves high-stakes corporate gifting challenges across India.',
    url: 'https://wrapyourwish.com/case-studies',
    hasPart: CASE_STUDIES_LIST.map((s) => ({
      '@type': 'Article',
      headline: s.title,
      url: s.seo.canonicalUrl,
    })),
  };

  return (
    <div className="bg-[#FAF6F1] min-h-screen text-[#2C2416]">
      <SEO
        title="Corporate Gifting Case Studies & Client Success Stories | WrapYourWish"
        description="Explore real-world B2B corporate gifting case studies across Delhi NCR & India. From urgent Diwali festive hampers to automated remote employee milestone kits."
        keywords="corporate gifting case studies, corporate gifting client stories, Diwali corporate gifting case study, remote employee gifting case study, B2B client retention case study, WrapYourWish reviews"
        canonicalUrl="https://wrapyourwish.com/case-studies"
        ogImage="https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&q=80&w=1200"
        schemas={[breadcrumbSchema, collectionSchema]}
      />

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#F4E7DA] to-[#FAF6F1] border-b border-[#E9DFD3] pt-12 pb-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          
          <nav aria-label="Breadcrumbs" className="flex items-center justify-center gap-2 text-xs text-[#7A5C4C] mb-6">
            <Link to="/" className="hover:text-[#C58B76] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-[#C58B76]/60" />
            <span className="text-[#2C2416] font-medium">Client Stories</span>
          </nav>

          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-[#C58B76]/15 text-[#8F4A32] border border-[#C58B76]/30 mb-4">
            <Award className="w-3.5 h-3.5 text-[#C58B76]" />
            Proven Impact &amp; Real Results
          </span>

          <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#2C2416] leading-tight mb-5 max-w-3xl mx-auto">
            Client Success Stories
          </h1>

          <p className="text-base sm:text-lg text-[#5B4A3A] leading-relaxed max-w-2xl mx-auto mb-10">
            A look at how thoughtful gifting, agile logistics, and bespoke curation moved the needle for brands and teams across industries.
          </p>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs font-medium tracking-wider transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-[#2C2416] text-[#FAF6F1] shadow-sm'
                    : 'bg-white border border-[#E9DFD3] text-[#5B4A3A] hover:bg-[#FAF6F1] hover:border-[#C58B76]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredStudies.map((study) => (
              <article
                key={study.slug}
                className="group bg-white rounded-2xl border border-[#EFE3D2] overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  {/* Card Image */}
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#F0E7DA]">
                    <img
                      src={study.heroImage}
                      alt={study.title}
                      width="600"
                      height="375"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-[#2C2416]/85 backdrop-blur-sm text-[#F4D5C5] text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full border border-white/10">
                      {study.badge}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6">
                    {/* Primary Metric Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-[#FAF6F1] border border-[#EFE3D2] text-xs font-semibold text-[#C58B76] mb-3">
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>{study.metrics[0].value} {study.metrics[0].label}</span>
                    </div>

                    <h2 className="font-serif-display text-xl text-[#2C2416] group-hover:text-[#C58B76] transition-colors leading-snug mb-3">
                      <Link to={`/case-studies/${study.slug}`}>
                        {study.title}
                      </Link>
                    </h2>

                    {/* Quote Excerpt */}
                    <div className="relative pl-3 border-l-2 border-[#C58B76]/50 mb-5">
                      <p className="text-xs text-[#5B4A3A] italic line-clamp-3 leading-relaxed">
                        "{study.quote}"
                      </p>
                      <div className="text-[11px] font-medium text-[#7A5C4C] mt-2">
                        &mdash; {study.quoteAuthor}
                      </div>
                    </div>

                    {/* Meta Tags */}
                    <div className="flex flex-wrap gap-2 text-[11px] text-[#7A5C4C] pt-3 border-t border-[#F0E7DA]">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-[#C58B76]" /> {study.clientIndustry}
                      </span>
                      <span>&bull;</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-[#C58B76]" /> {study.clientLocation}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card CTA Footer */}
                <div className="p-6 pt-0">
                  <Link
                    to={`/case-studies/${study.slug}`}
                    className="w-full inline-flex items-center justify-between px-5 py-3 rounded-xl bg-[#FAF6F1] text-[#2C2416] group-hover:bg-[#2C2416] group-hover:text-[#FAF6F1] text-xs uppercase tracking-widest font-semibold transition-all border border-[#EFE3D2] group-hover:border-[#2C2416]"
                  >
                    <span>Read Challenge &amp; Solution</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Partners Marquee */}
      <BrandsTicker />

      {/* Why Choose Wrap Your Wish Pillar Grid */}
      <section className="py-16 bg-white border-y border-[#E9DFD3]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-2 font-semibold">
              The Enterprise Advantage
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2C2416]">
              Why Leading Brands Trust Wrap Your Wish
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-[#FAF6F1] border border-[#EFE3D2]">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-[#C58B76] mb-4 border border-[#E9DFD3]">
                <Truck className="w-6 h-6" />
              </div>
              <h3 className="font-serif-display text-lg text-[#2C2416] mb-2">PAN India Logistics</h3>
              <p className="text-xs text-[#5B4A3A] leading-relaxed">
                Direct doorstep dispatch to 19,000+ pincodes across India with live courier tracking.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF6F1] border border-[#EFE3D2]">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-[#C58B76] mb-4 border border-[#E9DFD3]">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="font-serif-display text-lg text-[#2C2416] mb-2">In-House Customization</h3>
              <p className="text-xs text-[#5B4A3A] leading-relaxed">
                Laser engraving, digital foil stamping, and bespoke sleeve production completed in 24 hours.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF6F1] border border-[#EFE3D2]">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-[#C58B76] mb-4 border border-[#E9DFD3]">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="font-serif-display text-lg text-[#2C2416] mb-2">100% GST Billing</h3>
              <p className="text-xs text-[#5B4A3A] leading-relaxed">
                Streamlined vendor onboarding, credit terms for enterprises, and compliant tax invoices.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF6F1] border border-[#EFE3D2]">
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-[#C58B76] mb-4 border border-[#E9DFD3]">
                <HeartHandshake className="w-6 h-6" />
              </div>
              <h3 className="font-serif-display text-lg text-[#2C2416] mb-2">Dedicated Gifting Stylist</h3>
              <p className="text-xs text-[#5B4A3A] leading-relaxed">
                A single point of contact curating custom product catalogs and overseeing quality inspection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <ContactForm source="case-studies-hub" />
    </div>
  );
};

export default CaseStudiesIndex;
